import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Briefcase, MapPin, DollarSign, Clock, Building2 } from 'lucide-react';
import { getWorkTypeBadgeStyle, getWorkTypeLabel, formatSalary, getExpiryLabel } from '../utils/jobHelpers';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const JobsBrowse = () => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState({ industry: 'all', employmentType: 'all' });

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/jobs/listings?limit=200`);
      setJobs(response.data.jobs || []);
    } catch (error) {
      console.error('Failed to fetch jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredJobs = jobs.filter(job => {
    if (filter.industry !== 'all' && job.role_category !== filter.industry) return false;
    if (filter.employmentType !== 'all' && job.employment_type !== filter.employmentType) return false;
    if (search && !job.title.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-2">Browse Jobs</h1>
          <p className="text-muted-foreground text-lg">
            {loading ? 'Loading...' : `${filteredJobs.length} active job${filteredJobs.length !== 1 ? 's' : ''} available`}
          </p>
        </div>

        {/* Filters */}
        <div className="mb-6 flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <Input
              placeholder="Search jobs by title..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full"
            />
          </div>
          <select
            value={filter.industry}
            onChange={(e) => setFilter({ ...filter, industry: e.target.value })}
            className="px-4 py-2 border border-border rounded-lg bg-background"
          >
            <option value="all">All Industries</option>
            <option value="Technology">Technology</option>
            <option value="Healthcare">Healthcare</option>
            <option value="Finance">Finance</option>
            <option value="Retail">Retail</option>
            <option value="Construction">Construction</option>
            <option value="Education">Education</option>
            <option value="Hospitality">Hospitality</option>
          </select>
          <select
            value={filter.employmentType}
            onChange={(e) => setFilter({ ...filter, employmentType: e.target.value })}
            className="px-4 py-2 border border-border rounded-lg bg-background"
          >
            <option value="all">All Types</option>
            <option value="full_time">Full-time</option>
            <option value="part_time">Part-time</option>
            <option value="contract">Contract</option>
            <option value="casual">Casual</option>
          </select>
        </div>

        {/* Jobs Grid */}
        {loading ? (
          <div className="text-center py-12">Loading jobs...</div>
        ) : filteredJobs.length === 0 ? (
          <div className="text-center py-12">
            <Briefcase className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-xl font-semibold mb-2">No jobs found</h3>
            <p className="text-muted-foreground">Try adjusting your filters</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredJobs.map((job) => (
              <Card
                key={job.id}
                className="cursor-pointer hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-border hover:border-purple-300 dark:hover:border-purple-600"
                onClick={() => navigate(`/jobs/${job.job_code || job.id}`)}
              >
                <CardContent className="p-6 space-y-4">
                  {/* Job Code Badge */}
                  <div className="flex items-center justify-between mb-2">
                    <code className="text-xs px-2 py-1 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded font-mono text-blue-600 dark:text-blue-400">
                      {job.job_code || job.id.substring(0, 8)}
                    </code>
                    {job.is_boosted && (
                      <Badge className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white border-0">
                        ⚡ Boosted
                      </Badge>
                    )}
                  </div>
                  
                  <div>
                    <h3 className="font-bold text-lg mb-2 text-foreground line-clamp-2">{job.title}</h3>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                      <Building2 className="w-4 h-4" />
                      <span className="line-clamp-1">{job.employer?.company_name || 'Company'}</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <MapPin className="w-4 h-4" />
                    <span>{job.location_suburb}, {job.location_state}</span>
                  </div>

                  {job.salary_min && job.salary_max && (
                    <div className="flex items-center gap-2 text-sm font-semibold text-green-600 dark:text-green-400">
                      <DollarSign className="w-4 h-4" />
                      <span>{formatSalary(job.salary_min, job.salary_max, job.salary_unit)}</span>
                    </div>
                  )}

                  <div className="flex items-center justify-between pt-3 border-t border-border">
                    <Badge className={`${getWorkTypeBadgeStyle(job.work_type || job.employment_type)} border`}>
                      {getWorkTypeLabel(job.work_type || job.employment_type)}
                    </Badge>
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Clock className="w-3 h-3" />
                      <span>{getExpiryLabel(job.expires_at)}</span>
                    </div>
                  </div>

                  {job.applications_count > 0 && (
                    <div className="text-xs text-muted-foreground">
                      {job.applications_count} application{job.applications_count !== 1 ? 's' : ''}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default JobsBrowse;